import React from 'react';
import type { ShopifyCart, CalculationResult } from '../types';
import { formatCurrency } from '../utils/formatCurrency';

interface CartItemsProps {
  shopifyCart: ShopifyCart | null;
  calculationResult: CalculationResult | null;
  onQuantityChange: (itemId: number, newQuantity: number, delta: number) => void;
  onRemoveItem: (itemId: number, delta: number) => void;
  updatingQuantities?: Set<number>;
  removingItems?: Set<number>;
}

export const CartItems: React.FC<CartItemsProps> = ({
  shopifyCart,
  calculationResult,
  onQuantityChange,
  onRemoveItem,
  updatingQuantities = new Set(),
  removingItems = new Set()
}) => {
  if (!shopifyCart || !shopifyCart.items || shopifyCart.items.length === 0) {
    return (
      <div style={{
        padding: '20px',
        textAlign: 'center',
        color: '#666',
        backgroundColor: '#f9f9f9',
        borderRadius: '8px',
        marginBottom: '16px'
      }}>
        Your cart is empty
      </div>
    );
  }

  // Create a map of discounts by item key
  const discountMap = new Map<string, {
    discountAmount: number;
    finalPrice: number;
    discounts: Array<{ id: string; title: string; count: number }>;
  }>();

  if (calculationResult) {
    calculationResult.line_item_discounts.forEach((lineItem) => {
      const baseKey = lineItem.key.split('.')[0]; // Remove .001 suffix

      // Group discounts by ID and count occurrences
      const discountCounts = new Map<string, { title: string; count: number }>();
      lineItem.individual_applied_discounts.forEach((discount) => {
        const existing = discountCounts.get(discount.id);
        if (existing) {
          existing.count += 1;
        } else {
          discountCounts.set(discount.id, {
            title: discount.title,
            count: 1
          });
        }
      });

      // Convert to array with multipliers
      const discounts = Array.from(discountCounts.values()).map(d => ({
        id: '', // Not needed for display
        title: d.count > 1 ? `${d.title} ${d.count}x` : d.title,
        count: d.count
      }));

      discountMap.set(baseKey, {
        discountAmount: lineItem.total_discount_amount,
        finalPrice: 0, // Will calculate from items
        discounts: discounts,
      });
    });

    // Calculate final prices
    shopifyCart.items.forEach((item) => {
      const itemKey = item.key || `cart_item_${item.id}`;
      const baseKey = itemKey.split('.')[0];
      const discountInfo = discountMap.get(baseKey);

      if (discountInfo) {
        const originalTotal = (item.price * item.quantity) / 100;
        discountInfo.finalPrice = originalTotal - discountInfo.discountAmount;
      }
    });
  }

  return (
    <div className="intan-cart-items" style={{ marginBottom: '20px' }}>
      <h3 style={{
        fontSize: '16px',
        fontWeight: '600',
        marginBottom: '12px',
        color: '#333'
      }}>
        Your Shopping Cart
      </h3>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {shopifyCart.items.map((item, index) => {
          const itemKey = item.key || `cart_item_${item.id}`;
          const baseKey = itemKey.split('.')[0];
          const discountInfo = discountMap.get(baseKey);
          const hasDiscount = discountInfo && discountInfo.discountAmount > 0;

          const originalPrice = item.price / 100; // Convert cents to currency
          const originalTotal = originalPrice * item.quantity;
          const finalPrice = discountInfo ? discountInfo.finalPrice : originalTotal;
          const itemDiscount = discountInfo ? discountInfo.discountAmount : 0;
          const finalPricePerUnit = discountInfo ? finalPrice / item.quantity : originalPrice;
          const originalPricePerUnit = originalPrice;

          // Get product image
          let productImage = 'https://via.placeholder.com/100x100?text=No+Image';
          if (item.image) {
            productImage = item.image;
          } else if (item.featured_image) {
            if (typeof item.featured_image === 'string') {
              productImage = item.featured_image;
            } else if (item.featured_image.url) {
              productImage = item.featured_image.url;
            }
          }

          const handleQuantityChange = (delta: number) => {
            const newQuantity = Math.max(1, item.quantity + delta);
            onQuantityChange(item.id, newQuantity, delta);
          };

          const handleRemoveItem = () => {
            onRemoveItem(item.id, item.quantity * -1);
          };

          // Check if item is an addon or auto-added (should not be clickable and no quantity adjustment)
          // Match the same check used in cartMapper.ts
          const isAddon = item.properties?.Addon === 'Yes' || item.properties?.Addon === 'yes';
          const isAutoAdded = item.properties?._auto_added == true || item.properties?._auto_added === 'true';
          const isNonInteractive = isAddon || isAutoAdded;

          // Get product URL - Shopify cart items typically have a url field
          // If not available, construct from product_id
          // Don't make it clickable if it's an addon or auto-added
          let productUrl: string | null = null;
          if (!isNonInteractive) {
            if ((item as any).url) {
              productUrl = (item as any).url;
              // Ensure absolute URL for external links
              if (productUrl && !productUrl.startsWith('http') && !productUrl.startsWith('//')) {
                // If it's a relative URL, make it absolute using current origin
                productUrl = window.location.origin + (productUrl.startsWith('/') ? productUrl : '/' + productUrl);
              }
            } else if (item.product_id) {
              // Fallback: construct URL from product_id (may need handle for proper URL)
              const handle = (item as any).product_handle;
              if (handle) {
                productUrl = `${window.location.origin}/products/${handle}`;
              }
            }
          }

          // Filter properties that don't start with underscore
          const visibleProperties = item.properties ? 
            Object.entries(item.properties).filter(([key]) => !key.startsWith('_')) : [];

          const handleProductClick = (e: React.MouseEvent) => {
            if (productUrl && !isNonInteractive) {
              e.preventDefault();
              window.open(productUrl, '_blank', 'noopener,noreferrer');
            }
          };

          return (
            <div
              key={itemKey || index}
              style={{
                display: 'flex',
                gap: '12px',
                padding: '16px',
                backgroundColor: hasDiscount ? '#f0f9ff' : '#fff',
                border: hasDiscount ? '1px solid #b3d9ff' : '1px solid #e0e0e0',
                borderRadius: '8px',
                position: 'relative'
              }}
            >
              {hasDiscount && (
                <div style={{
                  position: 'absolute',
                  left: '-8px',
                  bottom: '12px',
                  backgroundColor: '#28a745',
                  color: '#fff',
                  fontSize: '10px',
                  fontWeight: '600',
                  padding: '4px 10px',
                  borderRadius: '3px 10px 10px 3px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.12)',
                  letterSpacing: '0.3px',
                  textTransform: 'uppercase',
                  transform: 'rotate(-4deg)',
                  transformOrigin: 'left bottom',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px'
                }}>
                  <span style={{
                    display: 'inline-block',
                    width: '0',
                    height: '0',
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderTop: '6px solid #fff',
                    opacity: 0.85,
                    marginRight: '2px'
                  }} />
                  Save {formatCurrency(itemDiscount)}
                </div>
              )}
              {/* Product Image */}
              <div 
                onClick={productUrl && !isNonInteractive ? handleProductClick : undefined}
                style={{
                  flexShrink: 0,
                  cursor: productUrl && !isNonInteractive ? 'pointer' : 'default'
                }}
              >
                <img
                  src={productImage}
                  alt={item.title}
                  style={{
                    width: '80px',
                    height: '80px',
                    objectFit: 'cover',
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0',
                    cursor: productUrl && !isNonInteractive ? 'pointer' : 'default'
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/80x80?text=No+Image';
                  }}
                />
              </div>

              {/* Product Info and Controls */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', position: 'relative' }}>
                {/* Remove Button */}
                <button
                  onClick={handleRemoveItem}
                  disabled={removingItems.has(item.id) || updatingQuantities.has(item.id)}
                  style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    width: '28px',
                    height: '28px',
                    border: 'none',
                    borderRadius: '50%',
                    backgroundColor: removingItems.has(item.id) ? '#f0f0f0' : '#f5f5f5',
                    color: '#666',
                    cursor: removingItems.has(item.id) || updatingQuantities.has(item.id) ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: '1',
                    opacity: removingItems.has(item.id) || updatingQuantities.has(item.id) ? 0.5 : 0.9,
                    transition: 'opacity 0.2s, background-color 0.2s, color 0.2s',
                    zIndex: 10,
                    padding: 0,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}
                  title="Remove item"
                  aria-label="Remove item"
                >
                  {removingItems.has(item.id) ? '⋯' : '×'}
                </button>

                {/* Product Title */}
                <div>
                  <div 
                    onClick={productUrl && !isNonInteractive ? handleProductClick : undefined}
                    style={{
                      fontWeight: '600',
                      marginBottom: '4px',
                      color: '#333',
                      fontSize: '15px',
                      cursor: productUrl && !isNonInteractive ? 'pointer' : 'default',
                      textDecoration: productUrl && !isNonInteractive ? 'none' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (productUrl && !isNonInteractive) {
                        (e.target as HTMLElement).style.textDecoration = 'underline';
                        (e.target as HTMLElement).style.color = '#0066cc';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (productUrl && !isNonInteractive) {
                        (e.target as HTMLElement).style.textDecoration = 'none';
                        (e.target as HTMLElement).style.color = '#333';
                      }
                    }}
                  >
                    {item.product_title}
                  </div>
                  {item.variant_title && (
                    <div style={{
                      color: '#666',
                      fontWeight: 'normal',
                      fontSize: '13px'
                    }}>
                      {item.variant_title}
                    </div>
                  )}

                  {/* Display properties that don't start with underscore */}
                  {visibleProperties.length > 0 && (
                    <div style={{
                      marginTop: '6px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px'
                    }}>
                      {visibleProperties.map(([key, value]) => (
                        <div 
                          key={key}
                          style={{
                            fontSize: '12px',
                            color: '#666'
                          }}
                        >
                          <span style={{ fontWeight: '500' }}>{key}:</span> {String(value)}
                        </div>
                      ))}
                    </div>
                  )}

                  {hasDiscount && (
                    <div style={{
                      fontSize: '12px',
                      color: '#28a745',
                      marginTop: '6px',
                      fontWeight: '500'
                    }}>
                      {discountInfo.discounts.map((discount, idx) => (
                        <div key={idx} style={{ marginTop: idx > 0 ? '4px' : '0' }}>
                          ✓ {discount.title}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Quantity Selector and Prices */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 'auto'
                }}>
                  {/* Quantity Selector - Hidden for addons and auto-added items */}
                  {!isNonInteractive && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '2px',
                      padding: '2px 4px',
                      borderRadius: '999px',
                      backgroundColor: '#fafafa',
                      border: '1px solid #e6e6e6',
                      boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
                    }}>
                      <button
                        onClick={() => handleQuantityChange(-1)}
                        disabled={item.quantity <= 1 || updatingQuantities.has(item.id)}
                        style={{
                          width: '22px',
                          height: '22px',
                          border: 'none',
                          borderRadius: '50%',
                          backgroundColor: '#fff',
                          color: '#161823',
                          cursor: updatingQuantities.has(item.id) ? 'wait' : (item.quantity <= 1 ? 'not-allowed' : 'pointer'),
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          lineHeight: '1',
                          opacity: updatingQuantities.has(item.id) || item.quantity <= 1 ? 0.5 : 1,
                          transition: 'opacity 0.2s, background-color 0.2s'
                        }}
                      >
                        −
                      </button>
                      <span style={{
                        minWidth: '24px',
                        textAlign: 'center',
                        fontWeight: '500',
                        fontSize: '13px',
                        color: '#161823',
                        opacity: updatingQuantities.has(item.id) ? 0.6 : 1,
                        transition: 'opacity 0.2s'
                      }}>
                        {item.quantity}
                        {/*{updatingQuantities.has(item.id) && (
                          <span style={{
                            marginLeft: '4px',
                            fontSize: '10px',
                            color: '#999'
                          }}>
                            ...
                          </span>
                        )}*/}
                      </span>
                      <button
                        onClick={() => handleQuantityChange(1)}
                        disabled={updatingQuantities.has(item.id)}
                        style={{
                          width: '22px',
                          height: '22px',
                          border: 'none',
                          borderRadius: '50%',
                          backgroundColor: '#fff',
                          color: '#161823',
                          cursor: updatingQuantities.has(item.id) ? 'wait' : 'pointer',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          lineHeight: '1',
                          opacity: updatingQuantities.has(item.id) ? 0.5 : 1,
                          transition: 'opacity 0.2s, background-color 0.2s'
                        }}
                      >
                        +
                      </button>
                    </div>
                  )}
                  {/* Show quantity as text for addons/auto-added items */}
                  {isNonInteractive && (
                    <div style={{
                      fontSize: '13px',
                      color: '#666',
                      fontWeight: '500'
                    }}>
                      Qty: {item.quantity}
                    </div>
                  )}

                  {/* Prices */}
                  <div style={{ textAlign: 'right', minWidth: '140px' }}>
                    {/* Single Price */}
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                      {hasDiscount ? (
                        <div>
                          <span style={{
                            textDecoration: 'line-through',
                            color: '#999',
                            marginRight: '6px',
                            fontSize: '11px'
                          }}>
                            {formatCurrency(originalPricePerUnit)}
                          </span>
                          <span style={{ color: '#28a745', fontWeight: '600' }}>
                            {formatCurrency(finalPricePerUnit)}
                          </span>
                          <span style={{ fontSize: '11px', color: '#666', marginLeft: '4px' }}>
                            each
                          </span>
                        </div>
                      ) : (
                        <span>{formatCurrency(originalPricePerUnit)} each</span>
                      )}
                    </div>

                    {/* Line Total */}
                    {hasDiscount ? (
                      <div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'baseline',
                          justifyContent: 'flex-end',
                          gap: '8px',
                          marginBottom: '2px'
                        }}>
                          <span style={{
                            fontSize: '11px',
                            color: '#999',
                            textDecoration: 'line-through'
                          }}>
                            {formatCurrency(originalTotal)}
                          </span>
                          <span style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#28a745'
                          }}>
                            {formatCurrency(finalPrice)}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#333'
                      }}>
                        {formatCurrency(originalTotal)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

